import React, { useState } from "react";
import { WizardStep } from "@sscs/types";
import { Dialog, Toast, Wizard } from "@sscs/ui";
import { getQueryStatus, getReleaseDateDisplay } from "@sscs/utils";
import { SoftwareInfo, Upload } from "Components";
import { DEFAULT_URL_IMPORT_DATA, VERSION_SOFTWARE_INFO_DATA } from "Constants";
import { useAppSelector, useFileUploadChecks } from "Hooks";
import {
  useLazyCheckUrlImportQuery,
  useLazyValidateUrlImportQuery,
} from "Services";
import { selectDeletedVersions } from "Store/projects.slice";
import {
  Package,
  UrlImportData,
  UrlImportForm,
  Version,
  VersionSoftwareInfo,
} from "Types";
import {
  apiErrorToast,
  getUrlImportFormDefaults,
  getVersionSoftwareInfoFormSchema,
  urlImportFormSchema,
  versionsToPredecessors,
} from "Utils";
import { useImportVersionFromUrlMutation } from "../../services/projects.api";
import styles from "./importVersion.module.scss";
import Overview from "./Overview";

type ImportVersionProps = {
  packageInfo: Package;
  versions: Version[];
  maxVersionsLimit: number;
  isOpen: boolean;
  onClose: () => void;
};

const ImportVersionContent = ({
  packageInfo,
  versions,
  maxVersionsLimit,
  onClose,
}: Omit<ImportVersionProps, "isOpen">): React.JSX.Element => {
  const [activeStep, setActiveStep] = useState(0);
  const [urlImportData, setUrlImportData] = useState<UrlImportData>(
    DEFAULT_URL_IMPORT_DATA,
  );
  const [softwareInfoData, setSoftwareInfoData] = useState<VersionSoftwareInfo>(
    {
      ...VERSION_SOFTWARE_INFO_DATA,
      ...(versions.length > 0 ? versions[0].software_info : {}),
      predecessor_id: versions[0]?.version_id ?? null,
      version: "",
      release_date: null,
    },
  );
  // Relying on cached data since validate must have been called during form validation
  const [validateUrl, { data: validatedUrlData }] =
    useLazyValidateUrlImportQuery();
  const [checkUrlImport, urlImportQuery] = useLazyCheckUrlImportQuery();
  const [importVersion, { isLoading: importingVersion }] =
    useImportVersionFromUrlMutation();
  const deletedVersions = useAppSelector(selectDeletedVersions);
  const {
    checksAreLoading,
    hasSufficientCapacity,
    isOverSizeLimit,
    remainingAfterUpload,
    remainingCapacity,
    sizeLimit,
  } = useFileUploadChecks("VERSION", urlImportQuery.data?.file_size ?? null);
  const checkIsSuccessful =
    hasSufficientCapacity && !isOverSizeLimit && !urlImportQuery.isError;

  const handleNext = () => {
    setActiveStep((previousState) => previousState + 1);
  };

  const handlePrevious = () => {
    setActiveStep((previousState) => previousState - 1);
  };

  const handleImport = async () => {
    try {
      const {
        predecessor_id,
        force,
        replace,
        notify_on_completion,
        ...software_info
      } = softwareInfoData;

      await importVersion({
        force,
        replace,
        notify_on_completion,
        body: {
          ...urlImportData,
          import_string: validatedUrlData?.purl ?? urlImportData.url,
          project_id: packageInfo.project_id,
          package_id: packageInfo.package_id,
          predecessor_id: predecessor_id ?? undefined,
          software_info,
        },
      }).unwrap();

      Toast.show({
        title: (
          <>
            Importing version <strong>{softwareInfoData.version}</strong>
          </>
        ),
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not import version");
    }
  };

  return (
    <Wizard
      title="Import version"
      subtitle="URL or PURL import"
      activeStep={activeStep}
      onStepChange={setActiveStep}
      dialogContentClasses={styles.dialog}
      steps={
        [
          {
            label: "Provide URL/PURL",
            onNext: async (data: UrlImportForm) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { authType, ...payload } = data;

              try {
                const { software_info } = await checkUrlImport({
                  ...payload,
                  url: validatedUrlData?.purl ?? payload.url,
                }).unwrap();

                if (software_info) {
                  setSoftwareInfoData((previous) => ({
                    ...previous,
                    ...(versions.length === 0 ? software_info : {}),
                    version: software_info.version,
                    release_date: getReleaseDateDisplay(
                      software_info.release_date,
                    ),
                    is_released: Boolean(software_info.release_date),
                  }));
                }
              } catch {
                // the error will be shown on the next step
              }
              setUrlImportData(payload);
              handleNext();
            },
            onPrevious: handlePrevious,
            formValues: getUrlImportFormDefaults(urlImportData),
            formMode: "onChange",
            schema: urlImportFormSchema(validateUrl),
            component: <Upload.UrlImportForm />,
            nextStep: { label: "Validate P/URL" },
          },
          {
            label: "Review usage",
            nextStep: {
              label: checkIsSuccessful ? undefined : "Close",
            },
            onNext: () => {
              if (checkIsSuccessful) {
                handleNext();
              } else {
                onClose();
              }
            },
            onPrevious: handlePrevious,
            component: (
              <Upload.UrlImportUsage
                queryStatus={
                  checksAreLoading
                    ? { status: "LOADING" }
                    : getQueryStatus(urlImportQuery)
                }
                fileSize={urlImportQuery.data?.file_size}
                hasSufficientCapacity={hasSufficientCapacity}
                isOverSizeLimit={isOverSizeLimit}
                remainingAfterImport={remainingAfterUpload}
                remainingQuota={remainingCapacity}
                uploadLimit={sizeLimit}
              />
            ),
          },
          {
            label: "Software information",
            onNext: (data: VersionSoftwareInfo) => {
              setSoftwareInfoData(data);
              handleNext();
            },
            onPrevious: handlePrevious,
            schema: getVersionSoftwareInfoFormSchema(
              versions
                .filter(
                  (version) =>
                    version.artifacts_summary.version.current_status.code ===
                    "DONE",
                )
                .map((version) => version.software_info.version),
            ),
            formValues: softwareInfoData,
            formProps: {
              horizontal: true,
              dense: true,
            },
            component: (
              <SoftwareInfo.Form
                importUrl={urlImportData.url}
                predecessors={versionsToPredecessors(versions, deletedVersions)}
                maxVersionsLimit={maxVersionsLimit}
                isVersion
              />
            ),
          },
          {
            label: "Overview",
            nextStep: { label: "Import", isLoading: importingVersion },
            onNext: async () => {
              await handleImport();
              onClose();
            },
            onPrevious: handlePrevious,
            component: (
              <Overview
                projectName={packageInfo.project_name}
                packageName={packageInfo.name}
                urlImportData={{
                  ...urlImportData,
                  url: validatedUrlData?.purl ?? urlImportData.url,
                }}
                softwareInfo={softwareInfoData}
                predecessorVersion={
                  versions.find(
                    (version) =>
                      version.version_id === softwareInfoData.predecessor_id,
                  )?.software_info.version
                }
                fileSize={urlImportQuery.data?.file_size ?? null}
                remainingAfterImport={remainingAfterUpload}
                showForce={versions.length >= maxVersionsLimit}
                showReplace={versions.some(
                  (version) =>
                    version.software_info.version === softwareInfoData.version,
                )}
              />
            ),
          },
        ] satisfies [
          WizardStep<UrlImportData>,
          WizardStep,
          WizardStep<VersionSoftwareInfo>,
          WizardStep,
        ]
      }
    />
  );
};

const ImportVersion = ({
  isOpen,
  ...props
}: ImportVersionProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose} size="md">
    <ImportVersionContent {...props} />
  </Dialog.Frame>
);

export default ImportVersion;
